import React, { useState, useEffect } from 'react';
import './App.css';
import { PutObjectCommand } from "@aws-sdk/client-s3";
import { Upload } from "@aws-sdk/lib-storage";
import { s3Client, BUCKET_NAME, CLOUDFRONT_DOMAIN, VIDEO_PREFIX, REGION } from "./aws-config";
import { saveData } from "./storageHelper";

const sanitizeFileName = (fileName) => fileName.replace(/\s+/g, '_');
const buildObjectKey = (folder, file) => `${folder}/${Date.now()}-${sanitizeFileName(file.name)}`;

const toUploadBody = async (file) => {
  if (file?.arrayBuffer) {
    return new Uint8Array(await file.arrayBuffer());
  }
  return file;
};

const buildUploadParams = async (folder, file) => {
  const key = buildObjectKey(folder, file);
  return {
    key,
    body: await toUploadBody(file),
    contentType: file.type,
  };
};

const buildPublicUrl = (key) => {
  if (CLOUDFRONT_DOMAIN) {
    return `https://${CLOUDFRONT_DOMAIN}/${key}`;
  }
  return `https://${BUCKET_NAME}.s3.${REGION}.amazonaws.com/${key}`;
};

function AdminPanel({
  services, setServices,
  projects, setProjects,
  btsPhotos, setBtsPhotos,
  btsVideos, setBtsVideos,
  trailers, setTrailers,
  movies, setMovies,
  founders, setFounders
}) {

  const [activeTab, setActiveTab] = useState('what-we-do');
  const [contactMessages, setContactMessages] = useState([]);
  const [newService, setNewService] = useState({ title: '', subtopics: [], imageFile: null });
  const [tempSubtopic, setTempSubtopic] = useState({ title: '', description: '' });
  const [newProject, setNewProject] = useState({ title: '', category: 'Movie', imageFile: null });
  const [newBtsPhoto, setNewBtsPhoto] = useState({ title: '', projectId: '', imageFile: null });
  const [newBtsVideo, setNewBtsVideo] = useState({ title: '', description: '', projectId: '', videoFile: null });
  const [newTrailer, setNewTrailer] = useState({ title: '', movie: '', videoFile: null, posterFile: null });
  const [newMovie, setNewMovie] = useState({ title: '' });
  const [newFounder, setNewFounder] = useState({ name: '', designation: '', description: '', mainImageFile: null, subImageFiles: [] });
  const [editingService, setEditingService] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [editingBtsPhoto, setEditingBtsPhoto] = useState(null);
  const [editingBtsVideo, setEditingBtsVideo] = useState(null);
  const [editingTrailer, setEditingTrailer] = useState(null);
  const [editingMovie, setEditingMovie] = useState(null);
  const [editingFounder, setEditingFounder] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    const storedMessages = JSON.parse(localStorage.getItem('contactMessages') || '[]');
    setContactMessages(storedMessages);
  }, []);

  const handleAddService = async (e) => {
    e.preventDefault();
    if (!newService.title) {
      alert('Title is required.');
      return;
    }
    if (!editingService && !newService.imageFile) {
      alert('Image is required for new services.');
      return;
    }
    setUploading(true);

    try {
      let imageUrl = newService.image; // Default to existing image if editing and no new file

      if (newService.imageFile) {
        const file = newService.imageFile;
        const { key: fileKey, body, contentType } = await buildUploadParams('services', file);

        const uploadParams = {
          Bucket: BUCKET_NAME,
          Key: fileKey,
          Body: body,
          ContentType: contentType,
        };

        await s3Client.send(new PutObjectCommand(uploadParams));
        imageUrl = buildPublicUrl(fileKey);
      }

      // Auto-generate link from title
      const autoLink = '/' + newService.title.toLowerCase().replace(/\s+/g, '-');

      let updatedServices;
      if (editingService) {
        // Update existing service
        let finalSubtopics = [...newService.subtopics];
        if (tempSubtopic.title.trim() && tempSubtopic.description.trim()) {
          finalSubtopics.push({ title: tempSubtopic.title.trim(), description: tempSubtopic.description.trim() });
        }

        const updatedService = {
          ...editingService,
          title: newService.title,
          subtopics: finalSubtopics,
          link: autoLink,
          image: imageUrl,
        };
        updatedServices = services.map(s => s.id === editingService.id ? updatedService : s);
        alert('Service updated successfully!');
      } else {
        // Create new service
        let finalSubtopics = [...newService.subtopics];
        if (tempSubtopic.title.trim() && tempSubtopic.description.trim()) {
          finalSubtopics.push({ title: tempSubtopic.title.trim(), description: tempSubtopic.description.trim() });
        }

        const service = {
          id: Date.now(),
          title: newService.title,
          subtopics: finalSubtopics,
          link: autoLink,
          image: imageUrl,
        };
        updatedServices = [...services, service];
        alert('Service added successfully!');
      }

      setServices(updatedServices);
      await saveData({ services: updatedServices, projects, btsPhotos, btsVideos, trailers, movies, founders });
      setNewService({ title: '', subtopics: [], imageFile: null });
      setTempSubtopic({ title: '', description: '' });
      setEditingService(null);
    } catch (err) {
      console.error("Error saving service:", err);
      alert('Error saving service. Please check the console.');
    } finally {
      setUploading(false);
    }
  };

  const handleEditService = (service) => {
    setEditingService(service);
    setNewService({
      title: service.title,
      subtopics: service.subtopics || [],
      image: service.image,
      imageFile: null
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingService(null);
    setNewService({ title: '', subtopics: [], imageFile: null });
    setTempSubtopic({ title: '', description: '' });
  };

  const handleAddProject = async (e) => {
    e.preventDefault();
    if (!newProject.title) return;
    if (!editingProject && !newProject.imageFile) {
      alert("Image is required for new projects.");
      return;
    }
    setUploading(true);

    try {
      let imageUrl = newProject.imageUrl; // Default to existing if editing

      if (newProject.imageFile) {
        const file = newProject.imageFile;
        const { key: fileKey, body, contentType } = await buildUploadParams('projects', file);
        await s3Client.send(new PutObjectCommand({ Bucket: BUCKET_NAME, Key: fileKey, Body: body, ContentType: contentType }));
        imageUrl = buildPublicUrl(fileKey);
      }

      let updatedProjects;
      if (editingProject) {
        const updatedProject = {
          ...editingProject,
          title: newProject.title,
          category: newProject.category,
          movie: newProject.movie,
          imageUrl: imageUrl
        };
        updatedProjects = projects.map(p => p.id === editingProject.id ? updatedProject : p);
        alert('Project updated successfully!');
      } else {
        const project = {
          id: Date.now(),
          title: newProject.title,
          category: newProject.category,
          movie: newProject.movie,
          imageUrl: imageUrl,
        };
        updatedProjects = [...projects, project];
        alert('Project added successfully!');
      }

      setProjects(updatedProjects);
      await saveData({ services, projects: updatedProjects, btsPhotos, btsVideos, trailers, movies, founders });
      setNewProject({ title: '', category: 'Movie', movie: '', imageFile: null });
      setEditingProject(null);
    } catch (err) {
      console.error("Error saving project:", err);
      alert('Error saving project. Please check the console.');
    } finally {
      setUploading(false);
    }
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    setNewProject({
      title: project.title,
      category: project.category,
      movie: project.movie || '',
      imageUrl: project.imageUrl,
      imageFile: null
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEditProject = () => {
    setEditingProject(null);
    setNewProject({ title: '', category: 'Movie', movie: '', imageFile: null });
  };

  const handleAddBtsPhoto = async (e) => {
    e.preventDefault();
    if (!newBtsPhoto.title) return;
    if (!editingBtsPhoto && !newBtsPhoto.imageFile) {
      alert("Image is required.");
      return;
    }
    setUploading(true);

    try {
      let imageUrl = newBtsPhoto.imageUrl; // Default if editing

      if (newBtsPhoto.imageFile) {
        const file = newBtsPhoto.imageFile;
        const { key: fileKey, body, contentType } = await buildUploadParams('bts-photos', file);
        await s3Client.send(new PutObjectCommand({ Bucket: BUCKET_NAME, Key: fileKey, Body: body, ContentType: contentType }));
        imageUrl = buildPublicUrl(fileKey);
      }

      let updatedPhotos;
      if (editingBtsPhoto) {
        const updatedPhoto = {
          ...editingBtsPhoto,
          projectId: parseInt(newBtsPhoto.projectId),
          title: newBtsPhoto.title,
          movie: newBtsPhoto.movie,
          imageUrl: imageUrl
        };
        updatedPhotos = btsPhotos.map(p => p.id === editingBtsPhoto.id ? updatedPhoto : p);
        alert('BTS Photo updated successfully!');
      } else {
        const photo = {
          id: Date.now(),
          projectId: parseInt(newBtsPhoto.projectId),
          title: newBtsPhoto.title,
          movie: newBtsPhoto.movie,
          imageUrl: imageUrl,
        };
        updatedPhotos = [...btsPhotos, photo];
        alert('BTS Photo added successfully!');
      }

      setBtsPhotos(updatedPhotos);
      await saveData({ services, projects, btsPhotos: updatedPhotos, btsVideos, trailers, movies, founders });
      setNewBtsPhoto({ title: '', projectId: '', movie: '', imageFile: null });
      setEditingBtsPhoto(null);
    } catch (err) {
      console.error("Error saving BTS photo:", err);
      alert('Error saving BTS photo. Please check the console.');
    } finally {
      setUploading(false);
    }
  };

  const handleEditBtsPhoto = (photo) => {
    setEditingBtsPhoto(photo);
    setNewBtsPhoto({
      title: photo.title,
      projectId: photo.projectId,
      movie: photo.movie || '',
      imageUrl: photo.imageUrl,
      imageFile: null
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEditBtsPhoto = () => {
    setEditingBtsPhoto(null);
    setNewBtsPhoto({ title: '', projectId: '', movie: '', imageFile: null });
  };

  const handleAddBtsVideo = async (e) => {
    e.preventDefault();
    if (!newBtsVideo.title || !newBtsVideo.description) return;
    if (!editingBtsVideo && !newBtsVideo.videoFile) {
      alert("Video file is required.");
      return;
    }
    setUploading(true);

    try {
      let videoUrl = newBtsVideo.videoUrl;

      if (newBtsVideo.videoFile) {
        const file = newBtsVideo.videoFile;
        const { key: fileKey, body, contentType } = await buildUploadParams(VIDEO_PREFIX, file);
        const uploadParams = { Bucket: BUCKET_NAME, Key: fileKey, Body: body, ContentType: contentType };

        const parallelUploads3 = new Upload({
          client: s3Client,
          params: uploadParams,
          leavePartsOnError: false,
        });
        parallelUploads3.on("httpUploadProgress", (progress) => {
          if (progress.total) {
            const percentage = Math.round((progress.loaded / progress.total) * 100);
            setUploadProgress(percentage);
          }
        });
        await parallelUploads3.done();
        videoUrl = buildPublicUrl(fileKey);
      }

      let updatedVideos;
      if (editingBtsVideo) {
        const updatedVideo = {
          ...editingBtsVideo,
          projectId: parseInt(newBtsVideo.projectId),
          title: newBtsVideo.title,
          description: newBtsVideo.description,
          movie: newBtsVideo.movie,
          videoUrl: videoUrl
        };
        updatedVideos = btsVideos.map(v => v.id === editingBtsVideo.id ? updatedVideo : v);
        alert('BTS Video updated successfully!');
      } else {
        const video = {
          id: Date.now(),
          projectId: parseInt(newBtsVideo.projectId),
          title: newBtsVideo.title,
          description: newBtsVideo.description,
          movie: newBtsVideo.movie,
          videoUrl: videoUrl,
        };
        updatedVideos = [...btsVideos, video];
        alert('BTS Video added successfully!');
      }

      setBtsVideos(updatedVideos);
      await saveData({ services, projects, btsPhotos, btsVideos: updatedVideos, trailers, movies, founders });
      setNewBtsVideo({ title: '', description: '', projectId: '', movie: '', videoFile: null });
      setUploadProgress(0);
      setEditingBtsVideo(null);
    } catch (err) {
      console.error("Error saving BTS video:", err);
      // ... error handling (simplified for this tool call, assume similar logic)
      let errorMessage = 'Error saving BTS video. ';
      if (err.message?.includes('Failed to fetch') || err.name === 'TypeError') errorMessage += 'CORS ERROR.';
      else if (err.code === 'AccessDenied') errorMessage += 'Access denied.';
      else errorMessage += err.message || 'Check console.';
      alert(errorMessage);
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const handleEditBtsVideo = (video) => {
    setEditingBtsVideo(video);
    setNewBtsVideo({
      title: video.title,
      description: video.description,
      projectId: video.projectId,
      movie: video.movie || '',
      videoUrl: video.videoUrl,
      videoFile: null
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEditBtsVideo = () => {
    setEditingBtsVideo(null);
    setNewBtsVideo({ title: '', description: '', projectId: '', movie: '', videoFile: null });
  };

  const handleDeleteService = async (id) => {
    const updatedServices = services.filter(service => service.id !== id);
    setServices(updatedServices);
    try {
      await saveData({ services: updatedServices, projects, btsPhotos, btsVideos, trailers, movies, founders });
    } catch (err) {
      console.error(err);
      alert("Failed to save changes: " + err.message);
    }
  };

  const handleDeleteProject = async (id) => {
    const updatedProjects = projects.filter(project => project.id !== id);
    setProjects(updatedProjects);
    try {
      await saveData({ services, projects: updatedProjects, btsPhotos, btsVideos, trailers, movies, founders });
    } catch (err) {
      console.error(err);
      alert("Failed to save changes: " + err.message);
    }
  };

  const handleDeleteBtsPhoto = async (id) => {
    const updatedPhotos = btsPhotos.filter(photo => photo.id !== id);
    setBtsPhotos(updatedPhotos);
    try {
      await saveData({ services, projects, btsPhotos: updatedPhotos, btsVideos, trailers, movies, founders });
    } catch (err) {
      console.error(err);
      alert("Failed to save changes: " + err.message);
    }
  };

  const handleDeleteBtsVideo = async (id) => {
    const updatedVideos = btsVideos.filter(video => video.id !== id);
    setBtsVideos(updatedVideos);
    try {
      await saveData({ services, projects, btsPhotos, btsVideos: updatedVideos, trailers, movies, founders });
    } catch (err) {
      console.error(err);
      alert("Failed to save changes: " + err.message);
    }
  };

  const handleAddTrailer = async (e) => {
    e.preventDefault();
    if (!newTrailer.title) return;

    if (!editingTrailer && !newTrailer.videoFile) {
      alert("Video file is required.");
      return;
    }
    setUploading(true);

    try {
      let videoUrl = newTrailer.videoUrl;
      let posterUrl = newTrailer.posterUrl; //add this line

      if (newTrailer.videoFile) {
        const file = newTrailer.videoFile;

        const { key: fileKey, body, contentType } = await buildUploadParams('trailers', file);
        const uploadParams = { Bucket: BUCKET_NAME, Key: fileKey, Body: body, ContentType: contentType };

        const parallelUploads3 = new Upload({
          client: s3Client,
          params: uploadParams,
          leavePartsOnError: false,
        });
        parallelUploads3.on("httpUploadProgress", (progress) => {
          if (progress.total) {
            const percentage = Math.round((progress.loaded / progress.total) * 100);
            setUploadProgress(percentage);
          }
        });
        await parallelUploads3.done();
        videoUrl = buildPublicUrl(fileKey);
      }

      //poster image upload 
      if(newTrailer.posterFile){
        const file = newTrailer.posterFile;
        const {key: fileKey, body, contentType} = await buildUploadParams('trailers/poster', file);

        const uploadParams = {
          Bucket: BUCKET_NAME,
          Key: fileKey,
          Body: body,
          ContentType: contentType,
        }

        const parallelUploads3 = new Upload({
          client: s3Client,
          params: uploadParams,
          leavePartsOnError: false,
        });

        await parallelUploads3.done();
        posterUrl = buildPublicUrl(fileKey);
      }

      let updatedTrailers;
      if (editingTrailer) {
        const updatedTrailer = {
          ...editingTrailer,
          title: newTrailer.title,
          movie: newTrailer.movie,
          videoUrl: videoUrl,
          posterUrl: posterUrl
        };
        updatedTrailers = trailers.map(t => t.id === editingTrailer.id ? updatedTrailer : t);
        alert('Trailer updated successfully!');
      } else {
        const trailer = {
          id: Date.now(),
          title: newTrailer.title,
          movie: newTrailer.movie,
          videoUrl: videoUrl,
          posterUrl: posterUrl,
        };
        updatedTrailers = [...trailers, trailer];
        alert('Trailer added successfully!');
      }

      setTrailers(updatedTrailers);
      await saveData({ services, projects, btsPhotos, btsVideos, trailers: updatedTrailers, movies, founders });
      setNewTrailer({ title: '', movie: '', videoFile: null, posterFile: null });
      setUploadProgress(0);
      setEditingTrailer(null);
    } catch (err) {
      console.error("Error saving trailer:", err);
      // ... error handling
      alert('Error saving trailer. Please check console.');
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const handleEditTrailer = (trailer) => {
    setEditingTrailer(trailer);
    setNewTrailer({
      title: trailer.title,
      movie: trailer.movie || '',
      videoUrl: trailer.videoUrl,
      posterUrl: trailer.posterUrl,
      videoFile: null,
      posterFile: null
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEditTrailer = () => {
    setEditingTrailer(null);
    setNewTrailer({ title: '', movie: '', videoFile: null, posterFile: null });
  };

  const handleDeleteTrailer = async (id) => {
    const updatedTrailers = trailers.filter(trailer => trailer.id !== id);
    setTrailers(updatedTrailers);
    try {
      await saveData({ services, projects, btsPhotos, btsVideos, trailers: updatedTrailers, movies, founders });
    } catch (err) {
      console.error(err);
      alert("Failed to save changes: " + err.message);
    }
  };

  const handleAddMovie = async (e) => {
    e.preventDefault();
    if (!newMovie.title.trim()) return;

    let updatedMovies;
    if (editingMovie) {
      const updatedMovie = { ...editingMovie, title: newMovie.title.trim() };
      updatedMovies = movies.map(m => m.id === editingMovie.id ? updatedMovie : m);
      alert('Movie updated successfully!');
    } else {
      const movie = { id: Date.now(), title: newMovie.title.trim() };
      updatedMovies = [...movies, movie];
      alert('Movie added successfully!');
    }

    setMovies(updatedMovies);
    await saveData({ services, projects, btsPhotos, btsVideos, trailers, movies: updatedMovies, founders });
    setNewMovie({ title: '' });
    setEditingMovie(null);
  };

  const handleEditMovie = (movie) => {
    setEditingMovie(movie);
    setNewMovie({ title: movie.title });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEditMovie = () => {
    setEditingMovie(null);
    setNewMovie({ title: '' });
  };

  const handleDeleteMovie = async (id) => {
    const updatedMovies = movies.filter(movie => movie.id !== id);
    setMovies(updatedMovies);
    await saveData({ services, projects, btsPhotos, btsVideos, trailers, movies: updatedMovies, founders });
  };

  const handleAddFounder = async (e) => {
    e.preventDefault();
    if (!newFounder.name) return;
    if (!editingFounder && !newFounder.mainImageFile) {
      alert("Main image is required.");
      return;
    }
    setUploading(true);

    try {
      // 1. Upload Main Image
      let mainImageUrl = newFounder.mainImage; // Default to existing
      if (newFounder.mainImageFile) {
        const mainFile = newFounder.mainImageFile;
        const { key: mainKey, body: mainBody, contentType: mainType } = await buildUploadParams('founders', mainFile);
        await s3Client.send(new PutObjectCommand({ Bucket: BUCKET_NAME, Key: mainKey, Body: mainBody, ContentType: mainType }));
        mainImageUrl = buildPublicUrl(mainKey);
      }

      // 2. Upload Sub Images
      const newSubImageUrls = [];
      if (newFounder.subImageFiles && newFounder.subImageFiles.length > 0) {
        for (let i = 0; i < newFounder.subImageFiles.length; i++) {
          const file = newFounder.subImageFiles[i];
          const { key, body, contentType } = await buildUploadParams('founders/sub', file);
          await s3Client.send(new PutObjectCommand({ Bucket: BUCKET_NAME, Key: key, Body: body, ContentType: contentType }));
          newSubImageUrls.push(buildPublicUrl(key));
        }
      }

      let updatedFounders;
      if (editingFounder) {
        // If editing, append new sub-images to existing ones
        const existingSubImages = editingFounder.subImages || [];
        const finalSubImages = [...existingSubImages, ...newSubImageUrls];

        const updatedFounder = {
          ...editingFounder,
          name: newFounder.name,
          designation: newFounder.designation,
          description: newFounder.description,
          mainImage: mainImageUrl,
          subImages: finalSubImages
        };
        updatedFounders = founders.map(f => f.id === editingFounder.id ? updatedFounder : f);
        alert('Founder updated successfully!');
      } else {
        const founder = {
          id: Date.now(),
          name: newFounder.name,
          designation: newFounder.designation,
          description: newFounder.description,
          mainImage: mainImageUrl,
          subImages: newSubImageUrls
        };
        updatedFounders = [...founders, founder];
        alert('Founder added successfully!');
      }

      setFounders(updatedFounders);
      await saveData({ services, projects, btsPhotos, btsVideos, trailers, movies, founders: updatedFounders });
      setNewFounder({ name: '', designation: '', description: '', mainImageFile: null, subImageFiles: [] });
      setEditingFounder(null);
    } catch (err) {
      console.error(err);
      alert('Error saving founder: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleEditFounder = (founder) => {
    setEditingFounder(founder);
    setNewFounder({
      name: founder.name,
      designation: founder.designation,
      description: founder.description,
      mainImage: founder.mainImage,
      mainImageFile: null,
      subImageFiles: [] // Reset file input
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEditFounder = () => {
    setEditingFounder(null);
    setNewFounder({ name: '', designation: '', description: '', mainImageFile: null, subImageFiles: [] });
  };

  const handleDeleteFounder = async (id) => {
    const updatedFounders = founders.filter(f => f.id !== id);
    setFounders(updatedFounders);
    await saveData({ services, projects, btsPhotos, btsVideos, trailers, movies, founders: updatedFounders });
  };

  const handleDeleteMessage = (id) => {
    const updatedMessages = contactMessages.filter(msg => msg.id !== id);
    setContactMessages(updatedMessages);
    localStorage.setItem('contactMessages', JSON.stringify(updatedMessages));
  };

  const renderContent = () => {
    switch (activeTab) {
      // ... (other cases remain unchanged, only checking contact-messages)
      case 'what-we-do':
        return (
          <div className="admin-section">
            <h3 className="section-heading">Manage Services</h3>
            <form onSubmit={handleAddService} className="admin-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="serviceTitle" style={{ color: '#FFFFFF' }}>Title</label>
                  <input
                    type="text"
                    id="serviceTitle"
                    value={newService.title}
                    onChange={(e) => setNewService({ ...newService, title: e.target.value })}
                    className="form-input"
                    placeholder="Service Title"
                    required
                  />
                </div>
                <div className="form-group">
                  <label style={{ color: '#FFFFFF' }}>Subtopics</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '10px' }}>
                    <input
                      type="text"
                      value={tempSubtopic.title}
                      onChange={(e) => setTempSubtopic({ ...tempSubtopic, title: e.target.value })}
                      className="form-input"
                      placeholder="Subtopic Title"
                    />
                    <textarea
                      value={tempSubtopic.description}
                      onChange={(e) => setTempSubtopic({ ...tempSubtopic, description: e.target.value })}
                      className="form-textarea"
                      placeholder="Subtopic Description"
                      style={{ minHeight: '60px' }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (tempSubtopic.title.trim() && tempSubtopic.description.trim()) {
                          setNewService({
                            ...newService,
                            subtopics: [...newService.subtopics, { title: tempSubtopic.title.trim(), description: tempSubtopic.description.trim() }]
                          });
                          setTempSubtopic({ title: '', description: '' });
                        }
                      }}
                      className="submit-btn"
                      style={{ width: 'auto', padding: '0 20px', alignSelf: 'flex-start' }}
                    >
                      Add Subtopic
                    </button>
                  </div>
                  <ul style={{ listStyle: 'none', padding: 0 }}>
                    {newService.subtopics.map((subtopic, index) => (
                      <li key={index} style={{ color: '#FFFFFF', marginBottom: '10px', background: '#333', padding: '10px', borderRadius: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <strong style={{ display: 'block', color: '#9933FF' }}>{subtopic.title || subtopic}</strong>
                          <span style={{ fontSize: '0.9em' }}>{subtopic.description || ''}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const updatedSubtopics = newService.subtopics.filter((_, i) => i !== index);
                            setNewService({ ...newService, subtopics: updatedSubtopics });
                          }}
                          style={{ background: 'transparent', border: 'none', color: '#ff4444', cursor: 'pointer', fontSize: '16px', marginLeft: '10px' }}
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="form-group">
                  <label htmlFor="serviceImageFile" style={{ color: '#FFFFFF' }}>Service Card Image</label>
                  <input
                    type="file"
                    id="serviceImageFile"
                    onChange={(e) => setNewService({ ...newService, imageFile: e.target.files[0] })}
                    className="form-input"
                    accept="image/*"
                  />
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" className="submit-btn" disabled={uploading}>
                  {uploading ? `Uploading...` : (editingService ? 'Update Service' : 'Add Service')}
                </button>
                {editingService && (
                  <button type="button" onClick={handleCancelEdit} className="cancel-btn">
                    Cancel Edit
                  </button>
                )}
              </div>
            </form>

            <div className="admin-list">
              {services.map((service) => (
                <div key={service.id} className="admin-item">
                  <div className="admin-item-content">
                    <h4 style={{ color: '#9933FF' }}>{service.title}</h4>
                    {service.subtopics && service.subtopics.length > 0 && (
                      <div style={{ paddingLeft: '10px', margin: '5px 0' }}>
                        {service.subtopics.map((sub, idx) => (
                          <div key={idx} style={{ marginBottom: '5px' }}>
                            <span style={{ color: '#FFFFFF', fontWeight: 'bold', fontSize: '0.9em' }}>{sub.title || sub}: </span>
                            <span style={{ color: '#FFFFFF', fontSize: '0.9em' }}>{sub.description || ''}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {service.image && <img src={service.image} alt={service.title} className="admin-item-image" />}
                  </div>
                  <button
                    onClick={() => handleEditService(service)}
                    className="edit-btn"
                    style={{ marginRight: '10px' }}
                  >
                    Edit
                  </button>
                  <button onClick={() => handleDeleteService(service.id)} className="delete-btn">Delete</button>
                </div>
              ))}
            </div>
          </div>
        );
      case 'projects':
        return (
          <div className="admin-section">
            <h3 className="section-heading">Manage Projects</h3>
            <form onSubmit={handleAddProject} className="admin-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="projectTitle" style={{ color: '#FFFFFF' }}>Title</label>
                  <input
                    type="text"
                    id="projectTitle"
                    value={newProject.title}
                    onChange={(e) => setNewProject({ ...newProject, title: e.target.value })}
                    className="form-input"
                    placeholder="Project Title"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="projectCategory" style={{ color: '#FFFFFF' }}>Category</label>
                  <select
                    id="projectCategory"
                    value={newProject.category}
                    onChange={(e) => setNewProject({ ...newProject, category: e.target.value })}
                    className="form-select"
                  >
                    <option value="Movie">Movie</option>
                    <option value="Web Series">Web Series</option>
                    <option value="Micro Dramas">Micro Dramas</option>
                    <option value="Reality Shows">Reality Shows</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="projectMovie" style={{ color: '#FFFFFF' }}>Movie (Optional - for Trailers)</label>
                  <select
                    id="projectMovie"
                    value={newProject.movie}
                    onChange={(e) => setNewProject({ ...newProject, movie: e.target.value })}
                    className="form-select"
                  >
                    <option value="">Select Movie</option>
                    {movies.map(movie => (
                      <option key={movie.id} value={movie.title}>{movie.title}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="projectImageFile" style={{ color: '#FFFFFF' }}>Image</label>
                  <input
                    type="file"
                    id="projectImageFile"
                    onChange={(e) => setNewProject({ ...newProject, imageFile: e.target.files[0] })}
                    className="form-input"
                    accept="image/*"
                    required={!editingProject}
                  />
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" className="submit-btn" disabled={uploading}>
                  {uploading ? 'Uploading...' : (editingProject ? 'Update Project' : 'Add Project')}
                </button>
                {editingProject && (
                  <button type="button" onClick={handleCancelEditProject} className="cancel-btn">
                    Cancel Edit
                  </button>
                )}
              </div>
            </form>

            <div className="admin-list">
              {projects.map((project) => (
                <div key={project.id} className="admin-item">
                  <div className="admin-item-content">
                    <h4 style={{ color: '#9933FF' }}>{project.title}</h4>
                    <p style={{ color: '#FFFFFF' }}>{project.category}</p>
                    {project.movie && <p style={{ color: '#FFFFFF', fontSize: '0.9em' }}>Movie: {project.movie}</p>}
                    <img src={project.imageUrl} alt={project.title} className="admin-item-image" />
                  </div>
                  <div>
                    <button onClick={() => handleEditProject(project)} className="edit-btn" style={{ marginRight: '10px' }}>Edit</button>
                    <button onClick={() => handleDeleteProject(project.id)} className="delete-btn">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      case 'bts-photos':
        return (
          <div className="admin-section">
            <h3 className="section-heading">Manage BTS Photos</h3>
            <form onSubmit={handleAddBtsPhoto} className="admin-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="btsPhotoTitle" style={{ color: '#FFFFFF' }}>Title</label>
                  <input
                    type="text"
                    id="btsPhotoTitle"
                    value={newBtsPhoto.title}
                    onChange={(e) => setNewBtsPhoto({ ...newBtsPhoto, title: e.target.value })}
                    className="form-input"
                    placeholder="Photo Title"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="btsPhotoProject" style={{ color: '#FFFFFF' }}>Project</label>
                  <select
                    id="btsPhotoProject"
                    value={newBtsPhoto.projectId}
                    onChange={(e) => {
                      const selectedProject = projects.find(p => p.id === parseInt(e.target.value));
                      setNewBtsPhoto({
                        ...newBtsPhoto,
                        projectId: e.target.value,
                        movie: selectedProject ? selectedProject.movie : ''
                      });
                    }}
                    className="form-select"
                    required
                  >
                    <option value="">Select Project</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>{project.title}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="btsPhotoImageFile" style={{ color: '#FFFFFF' }}>Image</label>
                  <input
                    type="file"
                    id="btsPhotoImageFile"
                    onChange={(e) => setNewBtsPhoto({ ...newBtsPhoto, imageFile: e.target.files[0] })}
                    className="form-input"
                    accept="image/*"
                    required={!editingBtsPhoto}
                  />
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" className="submit-btn" disabled={uploading}>
                  {uploading ? 'Uploading...' : (editingBtsPhoto ? 'Update Photo' : 'Add BTS Photo')}
                </button>
                {editingBtsPhoto && (
                  <button type="button" onClick={handleCancelEditBtsPhoto} className="cancel-btn">Cancel Edit</button>
                )}
              </div>
            </form>

            <div className="admin-list">
              {btsPhotos.map((photo) => (
                <div key={photo.id} className="admin-item">
                  <div className="admin-item-content">
                    <h4 style={{ color: '#9933FF' }}>{photo.title}</h4>
                    <p style={{ color: '#FFFFFF' }}>Project: {projects.find(p => p.id === photo.projectId)?.title || 'Unknown'}</p>
                    <img src={photo.imageUrl} alt={photo.title} className="admin-item-image" />
                  </div>
                  <div>
                    <button onClick={() => handleEditBtsPhoto(photo)} className="edit-btn" style={{ marginRight: '10px' }}>Edit</button>
                    <button onClick={() => handleDeleteBtsPhoto(photo.id)} className="delete-btn">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      case 'bts-videos':
        return (
          <div className="admin-section">
            <h3 className="section-heading">Manage BTS Videos</h3>
            <form onSubmit={handleAddBtsVideo} className="admin-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="btsVideoTitle" style={{ color: '#FFFFFF' }}>Title</label>
                  <input
                    type="text"
                    id="btsVideoTitle"
                    value={newBtsVideo.title}
                    onChange={(e) => setNewBtsVideo({ ...newBtsVideo, title: e.target.value })}
                    className="form-input"
                    placeholder="Video Title"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="btsVideoDescription" style={{ color: '#FFFFFF' }}>Description</label>
                  <textarea
                    id="btsVideoDescription"
                    value={newBtsVideo.description}
                    onChange={(e) => setNewBtsVideo({ ...newBtsVideo, description: e.target.value })}
                    className="form-textarea"
                    placeholder="Video Description"
                    required
                  ></textarea>
                </div>
                <div className="form-group">
                  <label htmlFor="btsVideoProject" style={{ color: '#FFFFFF' }}>Project</label>
                  <select
                    id="btsVideoProject"
                    value={newBtsVideo.projectId}
                    onChange={(e) => {
                      const selectedProject = projects.find(p => p.id === parseInt(e.target.value));
                      setNewBtsVideo({
                        ...newBtsVideo,
                        projectId: e.target.value,
                        movie: selectedProject ? selectedProject.movie : ''
                      });
                    }}
                    className="form-select"
                    required
                  >
                    <option value="">Select Project</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>{project.title}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="btsVideoFile" style={{ color: '#FFFFFF' }}>Video File</label>
                  <input
                    type="file"
                    id="btsVideoFile"
                    onChange={(e) => setNewBtsVideo({ ...newBtsVideo, videoFile: e.target.files[0] })}
                    className="form-input"
                    accept="video/*"
                    required={!editingBtsVideo}
                  />
                </div>
              </div>
              {uploading && (
                <div className="progress-bar-container">
                  <div className="progress-bar" style={{ width: `${uploadProgress}%` }}></div>
                  <span className="progress-text">{uploadProgress}%</span>
                </div>
              )}
              <div className="form-actions">
                <button type="submit" className="submit-btn" disabled={uploading}>
                  {uploading ? 'Uploading...' : (editingBtsVideo ? 'Update Video' : 'Add BTS Video')}
                </button>
                {editingBtsVideo && (
                  <button type="button" onClick={handleCancelEditBtsVideo} className="cancel-btn">Cancel Edit</button>
                )}
              </div>
            </form>

            <div className="admin-list">
              {btsVideos.map((video) => (
                <div key={video.id} className="admin-item">
                  <div className="admin-item-content">
                    <h4 style={{ color: '#9933FF' }}>{video.title}</h4>
                    <p style={{ color: '#FFFFFF' }}>{video.description}</p>
                    <p style={{ color: '#FFFFFF', fontSize: '0.9em' }}>Project: {projects.find(p => p.id === video.projectId)?.title || 'Unknown'}</p>
                    <video src={video.videoUrl} className="admin-item-video" controls />
                  </div>
                  <div>
                    <button onClick={() => handleEditBtsVideo(video)} className="edit-btn" style={{ marginRight: '10px' }}>Edit</button>
                    <button onClick={() => handleDeleteBtsVideo(video.id)} className="delete-btn">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      case 'trailers':
        return (
          <div className="admin-section">
            <h3 className="section-heading">Manage Trailers</h3>
            <form onSubmit={handleAddTrailer} className="admin-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="trailerTitle" style={{ color: '#FFFFFF' }}>Title</label>
                  <input
                    type="text"
                    id="trailerTitle"
                    value={newTrailer.title}
                    onChange={(e) => setNewTrailer({ ...newTrailer, title: e.target.value })}
                    className="form-input"
                    placeholder="Trailer Title"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="trailerMovie" style={{ color: '#FFFFFF' }}>Movie (Optional)</label>
                  <select
                    id="trailerMovie"
                    value={newTrailer.movie}
                    onChange={(e) => setNewTrailer({ ...newTrailer, movie: e.target.value })}
                    className="form-select"
                  >
                    <option value="">Select Movie</option>
                    {movies.map(movie => (
                      <option key={movie.id} value={movie.title}>{movie.title}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="trailerVideoFile" style={{ color: '#FFFFFF' }}>Video File</label>
                  <input
                    type="file"
                    id="trailerVideoFile"
                    onChange={(e) => setNewTrailer({ ...newTrailer, videoFile: e.target.files[0] })}
                    className="form-input"
                    accept="video/*"
                    required={!editingTrailer}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="trailerPosterFile" style={{ color: '#FFFFFF' }}>Poster image File</label>
                  <input
                    type="file"
                    id="trailerVideoFile"
                    onChange={(e) => setNewTrailer({ ...newTrailer, posterFile: e.target.files[0] })}
                    className="form-input"
                    accept="image/*"
                    required={!editingTrailer}
                  />
                </div>
              </div>
              {uploading && (
                <div className="progress-bar-container">
                  <div className="progress-bar" style={{ width: `${uploadProgress}%` }}></div>
                  <span className="progress-text">{uploadProgress}%</span>
                </div>
              )}
              <div className="form-actions">
                <button type="submit" className="submit-btn" disabled={uploading}>
                  {uploading ? 'Uploading...' : (editingTrailer ? 'Update Trailer' : 'Add Trailer')}
                </button>
                {editingTrailer && (
                  <button type="button" onClick={handleCancelEditTrailer} className="cancel-btn">Cancel Edit</button>
                )}
              </div>
            </form>

            <div className="admin-list">
              {trailers.map((trailer) => (
                <div key={trailer.id} className="admin-item">
                  <div className="admin-item-content">
                    <h4 style={{ color: '#9933FF' }}>{trailer.title}</h4>
                    {trailer.movie && 
                      <p 
                        style={{ color: '#FFFFFF', fontSize: '0.9em' }}>
                          Movie: {trailer.movie}
                      </p>
                    }
                    {console.log(`poster url console: `, trailer.posterUrl)}
                    {trailer.posterUrl && (
                      <img
                        src={trailer.posterUrl}
                        style={{ width: "180px", marginBottom: "10px" }}
                      />
                    )}
                    <video src={trailer.videoUrl} className="admin-item-video" controls />
                  </div>
                  <div>
                    <button onClick={() => handleEditTrailer(trailer)} className="edit-btn" style={{ marginRight: '10px' }}>Edit</button>
                    <button onClick={() => handleDeleteTrailer(trailer.id)} className="delete-btn">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      case 'movies':
        return (
          <div className="admin-section">
            <h3 className="section-heading">Manage Movies</h3>
            <form onSubmit={handleAddMovie} className="admin-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="movieTitle" style={{ color: '#FFFFFF' }}>Movie Title</label>
                  <input
                    type="text"
                    id="movieTitle"
                    value={newMovie.title}
                    onChange={(e) => setNewMovie({ ...newMovie, title: e.target.value })}
                    className="form-input"
                    placeholder="Enter movie title"
                    required
                  />
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" className="submit-btn">
                  {editingMovie ? 'Update Movie' : 'Add Movie'}
                </button>
                {editingMovie && (
                  <button type="button" onClick={handleCancelEditMovie} className="cancel-btn">Cancel Edit</button>
                )}
              </div>
            </form>

            <div className="admin-list">
              {movies.map((movie) => (
                <div key={movie.id} className="admin-item">
                  <div className="admin-item-content">
                    <h4 style={{ color: '#9933FF' }}>{movie.title}</h4>
                  </div>
                  <div>
                    <button onClick={() => handleEditMovie(movie)} className="edit-btn" style={{ marginRight: '10px' }}>Edit</button>
                    <button onClick={() => handleDeleteMovie(movie.id)} className="delete-btn">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      case 'contact-messages':
        return (
          <div className="admin-section">
            <h3 className="section-heading">Contact Messages</h3>
            <div className="admin-list">
              {contactMessages.length === 0 ? (
                <p style={{ color: '#FFFFFF' }}>No messages yet.</p>
              ) : (
                contactMessages.map((msg) => (
                  <div key={msg.id} className="admin-item" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                    <div className="admin-item-content" style={{ width: '100%' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                        <h4 style={{ color: '#9933FF', margin: 0 }}>{msg.name}</h4>
                        <span style={{ color: '#FFFFFF', fontSize: '0.9em' }}>{new Date(msg.date).toLocaleString()}</span>
                      </div>
                      <p style={{ color: '#FFFFFF', margin: '5px 0' }}><strong>Email:</strong> {msg.email}</p>
                      <p style={{ color: '#FFFFFF', margin: '5px 0' }}><strong>Phone:</strong> {msg.phone}</p>
                      <div style={{ background: '#333', padding: '10px', borderRadius: '5px', marginTop: '10px' }}>
                        <p style={{ color: '#FFFFFF', margin: 0 }}>{msg.message}</p>
                      </div>
                    </div>
                    <button onClick={() => handleDeleteMessage(msg.id)} className="delete-btn" style={{ marginTop: '15px' }}>Delete Message</button>
                  </div>
                ))
              )}
            </div>
          </div>
        );
      case 'founders':
        return (
          <div className="admin-section">
            <h3 className="section-heading">Manage Founders</h3>
            <form onSubmit={handleAddFounder} className="admin-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="founderName" style={{ color: '#FFFFFF' }}>Name</label>
                  <input
                    type="text"
                    id="founderName"
                    value={newFounder.name}
                    onChange={(e) => setNewFounder({ ...newFounder, name: e.target.value })}
                    className="form-input"
                    placeholder="Founder Name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="founderDesignation" style={{ color: '#FFFFFF' }}>Designation</label>
                  <input
                    type="text"
                    id="founderDesignation"
                    value={newFounder.designation}
                    onChange={(e) => setNewFounder({ ...newFounder, designation: e.target.value })}
                    className="form-input"
                    placeholder="Designation"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="founderDescription" style={{ color: '#FFFFFF' }}>Description</label>
                  <textarea
                    id="founderDescription"
                    value={newFounder.description}
                    onChange={(e) => setNewFounder({ ...newFounder, description: e.target.value })}
                    className="form-textarea"
                    placeholder="Short description"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="mainImageFile" style={{ color: '#FFFFFF' }}>Main Image</label>
                  <input
                    type="file"
                    id="mainImageFile"
                    onChange={(e) => setNewFounder({ ...newFounder, mainImageFile: e.target.files[0] })}
                    className="form-input"
                    accept="image/*"
                    required={!editingFounder}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="subImageFiles" style={{ color: '#FFFFFF' }}>Sub Images (Select up to 4)</label>
                  <input
                    type="file"
                    id="subImageFiles"
                    onChange={(e) => setNewFounder({ ...newFounder, subImageFiles: e.target.files })}
                    className="form-input"
                    accept="image/*"
                    multiple
                  />
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" className="submit-btn" disabled={uploading}>
                  {uploading ? 'Uploading...' : (editingFounder ? 'Update Founder' : 'Add Founder')}
                </button>
                {editingFounder && (
                  <button type="button" onClick={handleCancelEditFounder} className="cancel-btn">Cancel Edit</button>
                )}
              </div>
            </form>

            <div className="admin-list">
              {founders && founders.map((founder) => (
                <div key={founder.id} className="admin-item">
                  <div className="admin-item-content">
                    <h4 style={{ color: '#9933FF' }}>{founder.name}</h4>
                    <p style={{ color: '#FFFFFF' }}>{founder.designation}</p>
                    <img src={founder.mainImage} alt={founder.name} className="admin-item-image" />
                  </div>
                  <div>
                    <button onClick={() => handleEditFounder(founder)} className="edit-btn" style={{ marginRight: '10px' }}>Edit</button>
                    <button onClick={() => handleDeleteFounder(founder.id)} className="delete-btn">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h2 className="admin-title">Admin Dashboard</h2>
        <div className="admin-tabs">
          <button
            className={`admin-tab-button ${activeTab === 'what-we-do' ? 'active' : ''}`}
            onClick={() => setActiveTab('what-we-do')}
          >
            Services
          </button>
          <button
            className={`admin-tab-button ${activeTab === 'projects' ? 'active' : ''}`}
            onClick={() => setActiveTab('projects')}
          >
            Projects
          </button>
          <button
            className={`admin-tab-button ${activeTab === 'bts-photos' ? 'active' : ''}`}
            onClick={() => setActiveTab('bts-photos')}
          >
            BTS Photos
          </button>
          <button
            className={`admin-tab-button ${activeTab === 'bts-videos' ? 'active' : ''}`}
            onClick={() => setActiveTab('bts-videos')}
          >
            BTS Videos
          </button>
          <button
            className={`admin-tab-button ${activeTab === 'trailers' ? 'active' : ''}`}
            onClick={() => setActiveTab('trailers')}
          >
            Trailers
          </button>
          <button
            className={`admin-tab-button ${activeTab === 'movies' ? 'active' : ''}`}
            onClick={() => setActiveTab('movies')}
          >
            Movies
          </button>
          <button
            className={`admin-tab-button ${activeTab === 'contact-messages' ? 'active' : ''}`}
            onClick={() => setActiveTab('contact-messages')}
          >
            Messages
          </button>
          <button
            className={`admin-tab-button ${activeTab === 'founders' ? 'active' : ''}`}
            onClick={() => setActiveTab('founders')}
          >
            Founders
          </button>
        </div>
      </div>
      <div className="admin-content">
        {renderContent()}
      </div>
    </div>
  );
}

export default AdminPanel;
